import { PaymentOrderStatus, CashFundType, CheckType, TaxType, Period, Area, PaymentCategory, TaxBase, CheckStatus, TicketType, VatCondition, DocumentKey } from "../../../modules/administration"
import { MeasureType, PreparationStatus } from "../../../modules/common"
import { FiscalDocumentStatus, FiscalDocumentType } from "../../../modules/fiscal-documents"
import { AdjustmentType, PaymentStatus, TaxCategory } from "../../../modules/operations"
import { DeliveryMethod, DeliveryStatus } from "../../../modules/deliveries"
import { ProductType, LotAttribute } from "../../../modules/products"
import { CustomerCategory, HoardType } from "../../../modules/sales"
import { IdentifierType, RequestStatus, UserRole } from "../../../modules/system"

export const areaLabel = (area: Area) => {
    const labels = new Map<Area, string>()
        .set(Area.ADMINISTRATION, 'Administración')
        .set(Area.LOGISTICS, 'Logística')
        .set(Area.MANAGMENT, 'Dirección')    
        .set(Area.PURCHASES, 'Compras')
        .set(Area.SALES, 'Ventas')
        .set(Area.WAREHOUSE, 'Depósito')
        .set(Area.HR, 'Recursos Humanos')
    return labels.get(area)!
}

const userRoleLabel = (role: UserRole) => {
    const labels = new Map<UserRole, string>()
        .set(UserRole.ADMINISTRATION, 'Administración')
        .set(UserRole.LOGISTICS, 'Logística')
        .set(UserRole.MANAGER, 'Manager')    
        .set(UserRole.PURCHASES, 'Compras')
        .set(UserRole.SALES, 'Ventas')
        .set(UserRole.WAREHOUSE, 'Depósito')
    return labels.get(role)!
}

const identifierTypeLabel = (type: IdentifierType) => {
    const labels = new Map<IdentifierType, string>()
        .set(IdentifierType.PERSONAL_ID, 'DNI')    
        .set(IdentifierType.TAX_ID, 'CUIT / CUIL')
    return labels.get(type)!
}

const requestStatusLabel = (status: RequestStatus) => {
    const labels = new Map<RequestStatus, string>()
        .set(RequestStatus.APPROVED, 'Aprobada')
        .set(RequestStatus.DENIED, 'Rechazada')
        .set(RequestStatus.PENDING, 'Pendiente')
    return labels.get(status)!
}

const adjustmentTypeLabel = (type: AdjustmentType) => {
    const labels = new Map<AdjustmentType, string>()
        .set(AdjustmentType.DISCOUNT, 'Descuento')
        .set(AdjustmentType.SURCHARGE, 'Recargo')
    return labels.get(type)!
}

const hoardTypeLabel = (type: HoardType, summarized = false) => {
    const labels = new Map<HoardType, string>()
        .set(HoardType.FINANCIAL, `${summarized ? '' : 'Acopio '}Financiero`)
        .set(HoardType.PRODUCT, `${summarized ? 'De' : 'Acopio de'} Productos`)
    return labels.get(type)!
}

const deliveryStatusLabel = (status: DeliveryStatus) => {
    const labels = new Map<DeliveryStatus, string>()
        .set(DeliveryStatus.PENDING, 'Pendiente')
        .set(DeliveryStatus.PARTIALLY_DELIVERED, 'Entrega Parcial')
        .set(DeliveryStatus.DELIVERED, 'Entregado')
    return labels.get(status)!
}

const deliveryMethodLabel = (method?: DeliveryMethod) => {
    const labels = new Map<DeliveryMethod, string>()
        .set(DeliveryMethod.PICKUP, 'Retiro')
        .set(DeliveryMethod.RECEPTION, 'Recepción')
        .set(DeliveryMethod.RETURN, 'Devolución')
        .set(DeliveryMethod.SHIPMENT, 'Envío')
    return method ? labels.get((method) as DeliveryMethod)! : 'Entrega'
}

const lotAttributeLabel = (attr: LotAttribute) => {
    const labels = new Map<LotAttribute, string>()
        .set(LotAttribute.TONE, 'Tono')
        .set(LotAttribute.CALIBER, 'Calibre')
        .set(LotAttribute.DUE_DATE, 'Fecha de Vto.')
    return labels.get(attr)!
}

const periodLabel = (period: Period) => {
    const labels = new Map<Period, string>()
        .set(Period.DAILY, 'Diario')
        .set(Period.WEEKLY, 'Semanal')
        .set(Period.MONTHLY, 'Mensual')
        .set(Period.ANNUAL, 'Anual')
        .set(Period.PER_OPERATION, 'Por Operación')
    return labels.get(period)!
}

const preparationStatusLabel = (status: PreparationStatus) => {
    const labels = new Map<PreparationStatus, string>()
        .set(PreparationStatus.PENDING, 'Pendiente')
        .set(PreparationStatus.IN_PREPARATION, 'En Preparación')
        .set(PreparationStatus.READY, 'Listo')
    return labels.get(status)!
}

const taxTypeLabel = (type: TaxType) => {
    const labels = new Map<TaxType, string>()
        .set(TaxType.SALE_TAX, 'Impuesto de Venta')
        .set(TaxType.VAT, 'IVA')
        .set(TaxType.PERCEPTION, 'Percepción')
        .set(TaxType.RETENTION, 'Retención')
    return labels.get(type)!
}

const taxBaseLabel = (taxBase: TaxBase) => {
    const labels = new Map<TaxBase, string>()
        .set(TaxBase.PRICE_WITHOUT_VAT, 'Precio sin IVA')
        .set(TaxBase.VAT, 'IVA')
        .set(TaxBase.PRICE_WITH_VAT, 'Precio + IVA')
    return labels.get(taxBase)!
}

const cashFundTypeLabel = (type: CashFundType) => {
    const labels = new Map<CashFundType, string>()
        .set(CashFundType.CASH, 'Efectivo')    
        .set(CashFundType.BANCK_ACCOUNT, 'Cuenta de Banco')
    return labels.get(type)!
}

const checkTypeLabel = (type: CheckType) => {
    const labels = new Map<CheckType, string>()
        .set(CheckType.PHYSICAL, 'Físico')    
        .set(CheckType.E_CHEQ, 'E-Cheq')
    return labels.get(type)!
}

const checkStatusLabel = (status: CheckStatus) => {
    const labels = new Map<CheckStatus, string>()
        .set(CheckStatus.EMITTED, 'Emitido')
        .set(CheckStatus.DEFERRED, 'Diferido')
        .set(CheckStatus.REJECTED, 'Rechazado')
        .set(CheckStatus.ANNULLED, 'Anulado')
        .set(CheckStatus.ACTIVE_PENDING, 'Activo Pendiente')
        .set(CheckStatus.DISOWNED, 'Repudiado')
        .set(CheckStatus.PAYED, 'Pagado')
        .set(CheckStatus.ENDORSED, 'Endosado')
    return labels.get(status)!
}

const taxCategoryLabel = (taxCategory: TaxCategory) => {
    const labels = new Map<TaxCategory, string>()
        .set(TaxCategory.CONSUMER, 'Consumidor Final')
        .set(TaxCategory.REGISTERED, 'Responsable Inscripto')
        .set(TaxCategory.REGISTERED_AGENT, 'Ag. Ret. Responsable Inscripto')
        .set(TaxCategory.REGISTERED_FC, 'FCE Responsable Inscripto')
        .set(TaxCategory.EXEMPT, 'Exento')
        .set(TaxCategory.EXEMPT_FC, 'FCE Exento')
        .set(TaxCategory.SINGLE_TAXPAYER, 'Monotributista')
        .set(TaxCategory.M_TICKET, 'Repsonsable Factura M')
        .set(TaxCategory.NON_REGISTERED, 'No Inscripto')
    return labels.get(taxCategory)!
}

const customerCategoryLabel = (category: CustomerCategory) => {
    const labels = new Map<CustomerCategory, string>()
        .set(CustomerCategory.NATURAL, 'Persona Física')
        .set(CustomerCategory.LEGAL, 'Persona Jurídica')
    return labels.get(category)!
}

const measureTypeLabel = (type: MeasureType) => {
    const labels = new Map<MeasureType, string>()
        .set(MeasureType.AREA, 'Superficie')
        .set(MeasureType.DEPTH, 'Profundidad')
        .set(MeasureType.DIAMETER, 'Diametro')
        .set(MeasureType.HEIGHT, 'Altura')
        .set(MeasureType.LENGTH, 'Largo')
        .set(MeasureType.PACKAGE_UNITS, 'Unidades en Caja')
        .set(MeasureType.THICKNESS, 'Espesor')
        .set(MeasureType.VOLUME, 'Volumen')
        .set(MeasureType.WEIGHT, 'Peso')
        .set(MeasureType.WIDTH, 'Ancho')
    return labels.get(type)!
}

const ticketTypeLabel = (type: TicketType) => {
    const labels = new Map<TicketType, string>()
        .set(TicketType.TICKET_A, 'Factura A')    
        .set(TicketType.TICKET_B, 'Factura B (Consumidor Final)')    
        .set(TicketType.TICKET_C, 'Factura C (Monotributista)')
        .set(TicketType.RECEIPT_X, 'Recibo X')
        .set(TicketType.CREDIT_NOTE, 'Nota de Crédito')
        .set(TicketType.SALARY, 'Recibo de Sueldo')
    return labels.get(type)!
}

const paymentCategoryLabel = (category: PaymentCategory) => {
    const labels = new Map<PaymentCategory, string>()
        .set(PaymentCategory.CASH_FUND, 'Caja/Cuenta')
        .set(PaymentCategory.CHECK, 'Cheque')
        .set(PaymentCategory.CARD, 'Tarjeta')
        .set(PaymentCategory.CREDIT_ACCOUNT, 'Cuenta Corriente')
    return labels.get(category)!
}

const paymentOrderStatusLabel = (status: PaymentOrderStatus) => {
    const labels = new Map<PaymentOrderStatus, string>()
        .set(PaymentOrderStatus.EMITTED, 'Emitida')    
        .set(PaymentOrderStatus.PAYED, 'Pagada')
    return labels.get(status)!
}

const paymentStatusLabel = (status: PaymentStatus) => {
    const labels = new Map<PaymentStatus, string>()
        .set(PaymentStatus.PENDING, 'Pendiente')    
        .set(PaymentStatus.PARTIALLY_PAYED, 'Pago Parcial')
        .set(PaymentStatus.PAYED, 'Pagado')
    return labels.get(status)!
}

const fiscalDocumentStatusLabel = (status: FiscalDocumentStatus) => {
    const labels = new Map<FiscalDocumentStatus, string>()
        .set(FiscalDocumentStatus.PENDING, 'Pendiente')
        .set(FiscalDocumentStatus.PREVIEW, 'Previsualización')
        .set(FiscalDocumentStatus.TO_AUTHORIZE, 'Pendiente de Autorización')
        .set(FiscalDocumentStatus.AUTHORIZED, 'Autorizado')
        .set(FiscalDocumentStatus.PARTIALLY_INVOICED, 'Facturado Parcialmente')
        .set(FiscalDocumentStatus.REJECTED, 'Rechazado')
    return labels.get(status)!
}

const fiscalDocumentTypeLabel = (type: FiscalDocumentType) => {
    const labels = new Map<FiscalDocumentType, string>()
        .set(FiscalDocumentType.INVOICE, 'Factura')
        .set(FiscalDocumentType.CREDIT_NOTE, 'Nota de Crédito')
        .set(FiscalDocumentType.DEBIT_NOTE, 'Nota de Débito')
    return labels.get(type)!
}

// Badge derivado de `annulledAt`: no existe un estado fiscal CANCELLED.
const annulledFiscalDocumentLabel = 'Anulada'

const vatConditionLabel = (vatCondition: VatCondition) => {
    const labels = new Map<VatCondition, string>()
        .set(VatCondition.REGISTERED_TAXPAYER, 'IVA Responsable Inscripto')    
        .set(VatCondition.EXEMPT, 'IVA Sujeto Exento')
        .set(VatCondition.FINAL_CONSUMER, 'Consumidor Final')
        .set(VatCondition.SIMPLIFIED_TAXPAYER, 'Responsable Monotributo')
        .set(VatCondition.NON_CATEGORIZED, 'Sujeto No Categorizado')
        .set(VatCondition.FOREIGN_SUPPLIER, 'Proveedor del Exterior')
        .set(VatCondition.FOREIGN_CLIENT, 'Cliente del Exterior')
        .set(VatCondition.RELEASED, 'IVA Liberado')
        .set(VatCondition.SOCIAL_SIMPLIFIED_TAXPAYER, 'Monotributista Social')
        .set(VatCondition.NOT_APPLICABLE, 'IVA No Alcanzado')
        .set(VatCondition.PROMOTED_INDEPENDENT_SIMPLIFIED_TAXPAYER, 'Monotributista Trabajador Independiente Promovido')
    return labels.get(vatCondition)!
}

const productTypeLabel = (type: ProductType) => {
    const labels = new Map<ProductType, string>()
        .set(ProductType.UNITARY, 'Unitario')    
        .set(ProductType.PACKAGE, 'Cerámica/Porcellanato')
        .set(ProductType.BULK, 'Granel')
    return labels.get(type)!
}

const documentKeyLabel = (key: DocumentKey) => {
    const labels = new Map<DocumentKey, string>()
        .set(DocumentKey.BUDGET, 'Presupuesto')
        .set(DocumentKey.SALE, 'Orden de Venta')
        .set(DocumentKey.HOARD, 'Acopio')
        .set(DocumentKey.HOARD_STATUS, 'Estado de Acopio')
        .set(DocumentKey.PAYMENT_RECEIPT, 'Recibo de Pago')
        .set(DocumentKey.DELIVERY_NOTE, 'Remito')
    return labels.get(key)!
}

export const Labels = {
    areaLabel,
    userRoleLabel,
    identifierTypeLabel,
    requestStatusLabel,
    adjustmentTypeLabel,
    hoardTypeLabel,
    deliveryStatusLabel,
    deliveryMethodLabel,
    lotAttributeLabel,
    periodLabel,
    preparationStatusLabel,
    taxTypeLabel,
    taxBaseLabel,
    cashFundTypeLabel,
    checkTypeLabel,
    checkStatusLabel,
    taxCategoryLabel,
    customerCategoryLabel,
    measureTypeLabel,
    ticketTypeLabel,
    paymentCategoryLabel,
    paymentOrderStatusLabel,
    paymentStatusLabel,
    fiscalDocumentStatusLabel,
    fiscalDocumentTypeLabel,
    annulledFiscalDocumentLabel,
    vatConditionLabel,
    productTypeLabel,
    documentKeyLabel
}
